/**
 * Logo Slider block: scrolling strip.
 *
 * Markup: template-parts/blocks/logo-slider.php. Without this script, with
 * reduced motion, and when all logos fit (unless "always animate" is on), the
 * list stays a static row that wraps. While the strip moves, copies of the list
 * fill the space behind it; they are aria-hidden and inert, so screen readers
 * and the keyboard meet every logo once. The pause button is optional (filter
 * wombat_logo_slider_pause_button), so every use of this.toggle checks it.
 */

const reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

/**
 * One Logo Slider block.
 */
class LogoSlider {

	/**
	 * @param {HTMLElement} element Block root element (.b-logo-slider).
	 */
	constructor( element ) {

		this.element = element;
		this.viewport = element.querySelector( '.js-logo-slider-viewport' );
		this.list = element.querySelector( '.js-logo-slider-list' );
		this.toggle = element.querySelector( '.js-logo-slider-toggle' );
		this.toggleLabel = element.querySelector( '.js-logo-slider-toggle-label' );
		this.speed = Number( element.dataset.speed ) || 50;
		this.alwaysAnimate = element.dataset.alwaysAnimate === 'true';
		this.isGrid = element.dataset.layout === 'grid';
		this.isPaused = false;
		this.frame = null;

		// A grid becomes a strip below the breakpoint chosen in the editor.
		const breakpoint = Number( element.dataset.breakpoint );
		this.narrowScreen = this.isGrid && breakpoint ? window.matchMedia( `(width < ${ breakpoint }px)` ) : null;

		this.bind();
		this.update();

	}

	/**
	 * Attach listeners.
	 */
	bind() {

		this.toggle?.addEventListener( 'click', () => this.setPaused( ! this.isPaused ) );

		// While a logo has focus the strip stands still and the viewport scrolls to it (styles).
		// Leaving the logos returns the strip to its start, where the animation begins again.
		this.viewport.addEventListener( 'focusout', ( event ) => {

			if ( ! this.viewport.contains( event.relatedTarget ) ) {

				this.viewport.scrollLeft = 0;

			}

		} );

		reducedMotion.addEventListener( 'change', () => this.schedule() );
		this.narrowScreen?.addEventListener( 'change', () => this.schedule() );

		// The list grows when images load, the viewport when the window is resized.
		const observer = new ResizeObserver( () => this.schedule() );
		observer.observe( this.viewport );
		observer.observe( this.list );

	}

	/**
	 * Run update() once per frame, outside the ResizeObserver callback.
	 */
	schedule() {

		if ( this.frame === null ) {

			this.frame = window.requestAnimationFrame( () => this.update() );

		}

	}

	/**
	 * Decide whether the strip moves and prepare it.
	 */
	update() {

		this.frame = null;

		const isNarrow = this.narrowScreen?.matches ?? false;
		this.element.classList.toggle( 'b-logo-slider--narrow', isNarrow );

		if ( ( this.isGrid && ! isNarrow ) || reducedMotion.matches ) {

			this.stop();
			return;

		}

		// Measured from the items, so the result does not depend on whether the list wraps now.
		const items = [ ...this.list.children ];
		const gap = parseFloat( window.getComputedStyle( this.list ).columnGap ) || 0;
		const contentWidth = items.reduce( ( sum, item ) => sum + item.getBoundingClientRect().width, 0 ) + ( gap * Math.max( items.length - 1, 0 ) );
		const viewportWidth = this.viewport.clientWidth;

		if ( contentWidth === 0 || ( ! this.alwaysAnimate && contentWidth <= viewportWidth ) ) {

			this.stop();
			return;

		}

		this.start( contentWidth + gap, viewportWidth + gap );

	}

	/**
	 * Start the strip.
	 *
	 * Every list moves by its own width plus the gap, then starts again, so the
	 * copies have to cover the viewport while the first list moves out of it.
	 *
	 * @param {number} distance    Width of one list plus the gap, in pixels.
	 * @param {number} coverWidth  Viewport width plus the gap, in pixels.
	 */
	start( distance, coverWidth ) {

		this.setCopies( Math.max( 1, Math.ceil( coverWidth / distance ) ) );
		this.element.style.setProperty( '--logo-slider-duration', `${ ( distance / this.speed ).toFixed( 2 ) }s` );
		this.element.classList.add( 'b-logo-slider--marquee' );
		this.syncCopies();

		if ( this.toggle ) {

			this.toggle.hidden = false;

		}

	}

	/**
	 * Return to the static row.
	 */
	stop() {

		this.setCopies( 0 );
		this.element.classList.remove( 'b-logo-slider--marquee' );
		this.element.style.removeProperty( '--logo-slider-duration' );
		this.viewport.scrollLeft = 0;

		if ( ! this.toggle ) {

			return;

		}

		// Keep focus on the page when the button disappears under it.
		if ( this.toggle.contains( this.element.ownerDocument.activeElement ) ) {

			this.list.querySelector( 'a' )?.focus();

		}

		this.toggle.hidden = true;

	}

	/**
	 * @return {HTMLElement[]} Copies of the list.
	 */
	getCopies() {

		return [ ...this.viewport.querySelectorAll( '.b-logo-slider__list--clone' ) ];

	}

	/**
	 * Add or remove copies of the list.
	 *
	 * @param {number} count Number of copies.
	 */
	setCopies( count ) {

		const copies = this.getCopies();

		copies.slice( count ).forEach( ( copy ) => copy.remove() );

		for ( let i = copies.length; i < count; i++ ) {

			const copy = this.list.cloneNode( true );

			copy.classList.remove( 'js-logo-slider-list' );
			copy.classList.add( 'b-logo-slider__list--clone' );
			copy.setAttribute( 'aria-hidden', 'true' );
			copy.inert = true;
			copy.querySelectorAll( '[id]' ).forEach( ( node ) => node.removeAttribute( 'id' ) );

			this.viewport.append( copy );

		}

	}

	/**
	 * Align new copies with the running animation of the original list.
	 */
	syncCopies() {

		const [ original ] = this.list.getAnimations();

		if ( ! original ) {

			return;

		}

		this.getCopies().forEach( ( copy ) => {

			copy.getAnimations().forEach( ( animation ) => {

				animation.currentTime = original.currentTime;

			} );

		} );

	}

	/**
	 * @param {boolean} paused Whether the strip should stand still.
	 */
	setPaused( paused ) {

		this.isPaused = paused;
		this.element.classList.toggle( 'b-logo-slider--paused', paused );
		this.toggleLabel.textContent = paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;

	}

}

/**
 * Initialize all Logo Slider blocks on the page.
 */
const init = () => {

	[ ...document.querySelectorAll( '.b-logo-slider' ) ]
		.filter( ( element ) => element.querySelector( '.js-logo-slider-viewport' ) && element.querySelector( '.js-logo-slider-list' ) )
		.forEach( ( element ) => new LogoSlider( element ) );

};

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', init );

} else {

	init();

}
